import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Globe, Clock, Building2, Sparkles, CheckCircle2, Upload, Briefcase,
  ShieldCheck, HeartHandshake, ChevronDown,
} from "lucide-react";

const SKILLS = [
  "Customer Service", "Sales", "Appointment Setting", "Medical Billing",
  "Insurance Verification", "Bookkeeping", "IT Help Desk", "Active Directory",
  "Microsoft 365", "QuickBooks", "Salesforce", "HubSpot", "Apollo", "Dialpad",
  "Google Workspace", "Zoho", "Data Entry", "Executive Assistant",
];
const INDUSTRIES = [
  "Healthcare", "Legal", "Construction", "Property Management", "Finance",
  "Insurance", "Technology", "Retail", "Hospitality", "Manufacturing", "Other",
];
const SCHEDULES = ["US Eastern", "US Central", "US Mountain", "US Pacific", "Graveyard", "Flexible"];
const TIMEZONES = ["US Eastern", "US Central", "US Mountain", "US Pacific", "Flexible", "Other"];
const AVAILABILITY = ["Immediate", "2 Weeks", "30 Days", "Currently Employed", "Not Looking"];
const EDU = ["High School", "Associate", "Bachelor", "Master", "Doctorate", "Other"];
const ENGLISH = ["Native", "Fluent", "Professional", "Conversational", "Basic"];
const EMPLOYMENT = ["Full Time", "Part Time", "Contract"];

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const FAQ = [
  { q: "Is this a remote opportunity?", a: "Yes. The Rendeavored Talent Network connects you with remote roles supporting US-based businesses across multiple industries." },
  { q: "What does it cost?", a: "Nothing. Creating your profile is completely free." },
  { q: "What happens after I apply?", a: "Our team reviews your profile and reaches out when a current or future opportunity matches your experience." },
];

const Field = ({ label, children, required }) => (
  <div>
    <Label className="text-white/80 mb-1.5 block text-sm">
      {label} {required && <span className="text-[#66B2FF]">*</span>}
    </Label>
    {children}
  </div>
);

export default function Careers() {
  const formRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [resume, setResume] = useState(null);
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    country: "", state: "", city: "", time_zone: "",
    current_position: "", desired_positions: "", years_experience: "",
    highest_education: "", english_proficiency: "", additional_languages: "",
    availability: "", employment_preference: "", desired_salary: "", salary_currency: "USD",
    linkedin: "", portfolio: "", website: "",
    schedule: [], skills: [], industries: [], other_skills: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name || !f.last_name || !f.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setBusy(true);
    try {
      const skills = [...f.skills];
      f.other_skills.split(",").map((s) => s.trim()).filter(Boolean).forEach((s) => skills.push(s));
      const payload = {
        ...f,
        skills,
        additional_languages: f.additional_languages
          ? f.additional_languages.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        years_experience: f.years_experience ? Number(f.years_experience) : null,
        desired_salary: f.desired_salary ? Number(f.desired_salary) : null,
      };
      delete payload.other_skills;
      const fd = new FormData();
      fd.append("data", JSON.stringify(payload));
      if (resume) fd.append("resume", resume);
      const { candidateApply } = await import("@/lib/api");
      await candidateApply(fd);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // A 409 means this applicant is already in the Talent Network — that's a
      // friendly, expected outcome (not an error), so show the same confirmation
      // screen instead of a jarring red error toast.
      if (err?.response?.status === 409) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen rd-bg text-white flex items-center justify-center px-4 rd-noise">
        <div className="relative text-center max-w-md" data-testid="careers-success">
          <CheckCircle2 className="mx-auto text-[#2EE59D]" size={56} />
          <h1 className="text-2xl font-bold mt-5">You're in the Talent Network</h1>
          <p className="text-white/60 mt-3">
            Thank you for applying. Our team will reach out when a current or future
            opportunity matches your experience.
          </p>
          <div className="mt-8 flex justify-center">
            <Logo height={34} />
          </div>
        </div>
      </div>
    );
  }

  const inputCls = "bg-[#0A0F1E] border-white/10 h-11 text-white";

  return (
    <div className="min-h-screen rd-bg text-white">
      {/* Hero */}
      <header className="relative rd-noise overflow-hidden">
        <div className="absolute inset-0 rd-glow pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 pt-10 pb-16">
          <Logo height={34} />
          <div className="mt-16 max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[#66B2FF] text-sm bg-[#1E6BFF]/10 border border-[#1E6BFF]/25 rounded-full px-3 py-1">
              <Sparkles size={14} /> Rendeavored Talent Network
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mt-5 leading-tight">
              Join the Rendeavored <span className="text-[#66B2FF]">Talent Network</span>
            </h1>
            <p className="text-white/70 text-lg mt-4">
              Create your professional profile once and we'll match you with current and future
              remote opportunities across multiple industries.
            </p>
            <Button
              onClick={scrollToForm}
              data-testid="careers-apply-cta"
              className="mt-7 bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-12 px-8 text-base"
            >
              Apply Now
            </Button>
          </div>
        </div>
      </header>

      {/* Info sections */}
      <section className="max-w-5xl mx-auto px-6 py-12 grid sm:grid-cols-3 gap-5">
        {[
          { icon: HeartHandshake, t: "Why Rendeavored", d: "Meaningful remote work with growing US businesses that value your expertise." },
          { icon: Globe, t: "Remote Opportunities", d: "Work from anywhere, supporting teams across time zones and industries." },
          { icon: Building2, t: "Industries We Support", d: "Healthcare, Legal, Finance, Insurance, Technology, Property Management and more." },
        ].map((c) => (
          <div key={c.t} className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: "#0F1424" }}>
            <c.icon className="text-[#66B2FF]" size={22} />
            <h3 className="font-semibold mt-3">{c.t}</h3>
            <p className="text-white/60 text-sm mt-1.5">{c.d}</p>
          </div>
        ))}
      </section>

      {/* Hiring process */}
      <section className="max-w-5xl mx-auto px-6 pb-6">
        <h2 className="text-2xl font-bold mb-5">Hiring Process</h2>
        <div className="grid sm:grid-cols-4 gap-4">
          {["Apply & upload resume", "Profile review", "Interview & assessment", "Get matched & placed"].map((s, i) => (
            <div key={s} className="rounded-xl border border-white/10 p-4" style={{ backgroundColor: "#0F1424" }}>
              <div className="text-[#66B2FF] font-bold">{i + 1}</div>
              <div className="text-white/80 text-sm mt-1">{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section ref={formRef} className="max-w-3xl mx-auto px-6 py-12">
        <div className="rounded-2xl border border-white/10 p-6 sm:p-8" style={{ backgroundColor: "#0F1424" }}>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase size={20} className="text-[#66B2FF]" /> Apply to the Talent Network
          </h2>
          <p className="text-white/50 text-sm mt-1">Takes about 5 minutes. Fields marked * are required.</p>

          <form onSubmit={submit} className="mt-6 space-y-8" data-testid="careers-form">
            {/* Contact */}
            <div>
              <h3 className="text-white/70 font-semibold mb-3 text-sm uppercase tracking-wide">Contact</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="First name" required>
                  <Input data-testid="careers-first-name" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Last name" required>
                  <Input data-testid="careers-last-name" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Email" required>
                  <Input data-testid="careers-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Phone">
                  <Input data-testid="careers-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Country"><Input value={f.country} onChange={(e) => set("country", e.target.value)} className={inputCls} /></Field>
                <Field label="State / Province"><Input value={f.state} onChange={(e) => set("state", e.target.value)} className={inputCls} /></Field>
                <Field label="City"><Input value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></Field>
                <Field label="Time zone">
                  <Select value={f.time_zone} onValueChange={(v) => set("time_zone", v)}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </div>

            {/* Professional */}
            <div>
              <h3 className="text-white/70 font-semibold mb-3 text-sm uppercase tracking-wide">Professional</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Current position"><Input value={f.current_position} onChange={(e) => set("current_position", e.target.value)} className={inputCls} /></Field>
                <Field label="Desired position(s)"><Input data-testid="careers-desired" value={f.desired_positions} onChange={(e) => set("desired_positions", e.target.value)} className={inputCls} /></Field>
                <Field label="Years experience"><Input type="number" min="0" value={f.years_experience} onChange={(e) => set("years_experience", e.target.value)} className={inputCls} /></Field>
                <Field label="Highest education">
                  <Select value={f.highest_education} onValueChange={(v) => set("highest_education", v)}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{EDU.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="English proficiency">
                  <Select value={f.english_proficiency} onValueChange={(v) => set("english_proficiency", v)}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{ENGLISH.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Additional languages (comma separated)"><Input value={f.additional_languages} onChange={(e) => set("additional_languages", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>

            {/* Availability & schedule */}
            <div>
              <h3 className="text-white/70 font-semibold mb-3 text-sm uppercase tracking-wide">Availability</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Availability">
                  <Select value={f.availability} onValueChange={(v) => set("availability", v)}>
                    <SelectTrigger data-testid="careers-availability" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{AVAILABILITY.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Employment preference">
                  <Select value={f.employment_preference} onValueChange={(v) => set("employment_preference", v)}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{EMPLOYMENT.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="mt-4">
                <Label className="text-white/80 mb-2 block text-sm flex items-center gap-1.5"><Clock size={14} /> Schedule (select all that apply)</Label>
                <div className="flex flex-wrap gap-2">
                  {SCHEDULES.map((s) => {
                    const on = f.schedule.includes(s);
                    return (
                      <button type="button" key={s} onClick={() => set("schedule", toggle(f.schedule, s))}
                        className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${on ? "bg-[#1E6BFF] border-[#1E6BFF] text-white" : "border-white/15 text-white/70 hover:border-white/35"}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <Field label="Desired monthly salary"><Input type="number" min="0" value={f.desired_salary} onChange={(e) => set("desired_salary", e.target.value)} className={inputCls} /></Field>
                <Field label="Currency"><Input value={f.salary_currency} onChange={(e) => set("salary_currency", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>

            {/* Skills & industries */}
            <div>
              <h3 className="text-white/70 font-semibold mb-3 text-sm uppercase tracking-wide">Skills & Experience</h3>
              <Label className="text-white/80 mb-2 block text-sm">Skills</Label>
              <div className="flex flex-wrap gap-2">
                {SKILLS.map((s) => {
                  const on = f.skills.includes(s);
                  return (
                    <button type="button" key={s} onClick={() => set("skills", toggle(f.skills, s))}
                      className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${on ? "bg-[#1E6BFF] border-[#1E6BFF] text-white" : "border-white/15 text-white/70 hover:border-white/35"}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
              <Input placeholder="Other skills (comma separated)" value={f.other_skills} onChange={(e) => set("other_skills", e.target.value)} className={`${inputCls} mt-3`} />
              <Label className="text-white/80 mb-2 mt-5 block text-sm">Industry experience</Label>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((s) => {
                  const on = f.industries.includes(s);
                  return (
                    <button type="button" key={s} onClick={() => set("industries", toggle(f.industries, s))}
                      className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${on ? "bg-[#1E6BFF] border-[#1E6BFF] text-white" : "border-white/15 text-white/70 hover:border-white/35"}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Resume + links */}
            <div>
              <h3 className="text-white/70 font-semibold mb-3 text-sm uppercase tracking-wide">Resume & Links</h3>
              <label className="flex items-center gap-3 rounded-xl border border-dashed border-white/20 p-4 cursor-pointer hover:border-[#1E6BFF]/50 transition-colors" data-testid="careers-resume-label">
                <Upload size={18} className="text-[#66B2FF]" />
                <span className="text-white/70 text-sm">
                  {resume ? resume.name : "Upload resume (PDF, DOCX, DOC \u2014 max 12MB)"}
                </span>
                <input type="file" accept=".pdf,.docx,.doc" className="hidden" data-testid="careers-resume-input"
                  onChange={(e) => setResume(e.target.files?.[0] || null)} />
              </label>
              <div className="grid sm:grid-cols-3 gap-4 mt-4">
                <Field label="LinkedIn"><Input value={f.linkedin} onChange={(e) => set("linkedin", e.target.value)} className={inputCls} /></Field>
                <Field label="Portfolio"><Input value={f.portfolio} onChange={(e) => set("portfolio", e.target.value)} className={inputCls} /></Field>
                <Field label="Website"><Input value={f.website} onChange={(e) => set("website", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>

            <Button type="submit" disabled={busy} data-testid="careers-submit"
              className="w-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-12 text-base">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <><ShieldCheck size={18} className="mr-2" /> Submit application</>}
            </Button>
          </form>
        </div>

        {/* FAQ */}
        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-4">FAQ</h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div key={i} className="rounded-xl border border-white/10 overflow-hidden" style={{ backgroundColor: "#0F1424" }}>
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left">
                  <span className="text-white/85">{item.q}</span>
                  <ChevronDown size={18} className={`text-white/50 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="px-5 pb-4 text-white/60 text-sm">{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-white/40 text-xs">
        &copy; 2026 Rendeavored Group, LLC &bull; Rendeavored Talent Network
      </footer>
    </div>
  );
}
